<script setup>
import CalendarBlankIcon from '@iconify-vue/ph/calendar-blank';
import CaretLeftIcon from '@iconify-vue/ph/caret-left';
import CaretRightIcon from '@iconify-vue/ph/caret-right';
import {
    getLocalTimeZone,
    isSameMonth,
    isToday,
    startOfMonth,
    startOfWeek,
    today,
} from '@internationalized/date';
import ActionButton from '@js/components/buttons/ActionButton.vue';
import SpinnerIcon from '@js/components/icons/SpinnerIcon.vue';
import { isDate } from '@js/utils/date';
import { computed, ref } from 'vue';

const TIME_ZONE = getLocalTimeZone();

const WEEK_DAYS = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];

const {
    items = [],
    itemKey = 'id',
    dateKey = 'date',
    canMove = null,
    loading = false,
} = defineProps({
    items: {
        type: Array,
        required: false,
    },
    itemKey: {
        type: String,
        required: false,
    },
    /**
     * Property holding the item's date. Both a `YYYY-MM-DD…` string (the format the API
     * returns) and a `Date` object are accepted.
     */
    dateKey: {
        type: String,
        required: false,
    },
    /**
     * `(item) => bool`: which items can be dragged. Without this function the calendar stays
     * read-only.
     */
    canMove: {
        type: Function,
        required: false,
    },
    loading: {
        type: Boolean,
        required: false,
    },
});

const emit = defineEmits(['move']);

/**
 * Displayed month. The parent can drive it (`v-model:month`) to reload its data, or leave it
 * to the calendar, which then manages it alone.
 */
const month = defineModel('month', {
    // `defineModel` options are hoisted out of setup: no local constant here.
    default: () => startOfMonth(today(getLocalTimeZone())),
});

const draggedItem = ref(null);

const dragOverDay = ref(null);

const monthLabel = computed(() =>
    new Intl.DateTimeFormat('fr-FR', {
        month: 'long',
        year: 'numeric',
    }).format(month.value.toDate(TIME_ZONE)),
);

/**
 * Always six weeks, even when the month spans five: the grid then keeps a constant height
 * from one month to the next, and navigating no longer makes the page jump.
 */
const days = computed(() => {
    const gridStart = startOfWeek(month.value, 'fr-FR');

    return Array.from({ length: 42 }, (_, index) =>
        gridStart.add({ days: index }),
    );
});

function getDayKey(value) {
    if (!value) {
        return null;
    }

    if (isDate(value)) {
        return [
            value.getFullYear(),
            String(value.getMonth() + 1).padStart(2, '0'),
            String(value.getDate()).padStart(2, '0'),
        ].join('-');
    }

    return String(value).slice(0, 10);
}

const itemsByDay = computed(() => {
    const grouped = {};

    items.forEach((item) => {
        const day = getDayKey(item[dateKey]);

        if (day) {
            (grouped[day] ??= []).push(item);
        }
    });

    return grouped;
});

function getDayItems(day) {
    return itemsByDay.value[day.toString()] ?? [];
}

function goToMonth(offset) {
    month.value = month.value.add({ months: offset });
}

function goToCurrentMonth() {
    month.value = startOfMonth(today(TIME_ZONE));
}

function endDrag() {
    draggedItem.value = null;
    dragOverDay.value = null;
}

/**
 * The move is handed to the parent rather than applied here: only it knows what a date means
 * for its data, and what has to be persisted.
 */
function drop(day) {
    const item = draggedItem.value;

    endDrag();

    if (item && getDayKey(item[dateKey]) !== day.toString()) {
        emit('move', item, day);
    }
}

/**
 * Attributes to spread on the element the parent renders (`v-bind="dragProps"`): the parent
 * decides its own markup, the calendar only makes it draggable.
 */
function getDragProps(item) {
    if (!canMove || !canMove(item)) {
        return { draggable: false };
    }

    return {
        draggable: true,
        onDragstart: () => (draggedItem.value = item),
        onDragend: endDrag,
    };
}
</script>

<template>
    <div class="rounded-sm border border-gray-300 bg-white">
        <div
            class="flex flex-wrap items-center justify-between gap-2 border-b border-gray-300 px-4 py-3"
        >
            <SpinnerIcon
                v-if="loading"
                class="size-5 animate-spin fill-indigo-600 text-gray-200"
            />

            <div
                v-else
                class="flex w-full flex-wrap items-center justify-between gap-4"
            >
                <div class="flex flex-wrap items-center gap-2">
                    <ActionButton
                        size="small"
                        type="secondary"
                        :icon="CaretLeftIcon"
                        title="Mois précédent"
                        :callback="() => goToMonth(-1)"
                    />

                    <span class="min-w-44 text-center font-semibold capitalize">
                        {{ monthLabel }}
                    </span>

                    <ActionButton
                        size="small"
                        type="secondary"
                        :icon="CaretRightIcon"
                        title="Mois suivant"
                        :callback="() => goToMonth(1)"
                    />

                    <ActionButton
                        size="small"
                        type="secondary"
                        :left-icon="CalendarBlankIcon"
                        :callback="goToCurrentMonth"
                    >
                        Aujourd'hui
                    </ActionButton>
                </div>

                <div class="flex flex-wrap items-center gap-2">
                    <slot name="actions" />
                </div>
            </div>
        </div>

        <div class="grid grid-cols-7 border-b border-gray-300 bg-gray-50">
            <div
                v-for="weekDay in WEEK_DAYS"
                :key="weekDay"
                class="px-2 py-2 text-center text-xs font-semibold text-gray-500"
            >
                {{ weekDay }}
            </div>
        </div>

        <div class="grid grid-cols-7">
            <div
                v-for="day in days"
                :key="day.toString()"
                class="group relative min-h-28 border-r border-b border-gray-200 p-1 duration-150 last:border-r-0"
                :class="{
                    'bg-gray-50': !isSameMonth(day, month),
                    'bg-indigo-50':
                        dragOverDay && day.compare(dragOverDay) === 0,
                }"
                @dragover.prevent="dragOverDay = day"
                @drop.prevent="drop(day)"
            >
                <div class="mb-1 flex items-center justify-between">
                    <span
                        class="flex size-6 items-center justify-center rounded-full text-xs font-semibold"
                        :class="{
                            'bg-indigo-600 text-white': isToday(day, TIME_ZONE),
                            'text-gray-400':
                                !isToday(day, TIME_ZONE) &&
                                !isSameMonth(day, month),
                            'text-gray-700':
                                !isToday(day, TIME_ZONE) &&
                                isSameMonth(day, month),
                        }"
                    >
                        {{ day.day }}
                    </span>

                    <slot name="day" :day="day" />
                </div>

                <div class="flex flex-col gap-1">
                    <template
                        v-for="item in getDayItems(day)"
                        :key="item[itemKey]"
                    >
                        <slot
                            name="item"
                            :item="item"
                            :day="day"
                            :drag-props="getDragProps(item)"
                            :is-dragging="draggedItem === item"
                        />
                    </template>
                </div>
            </div>
        </div>
    </div>
</template>
